import { useEffect, useState } from "react"
import {
  arrayUnion,
  collection,
  deleteDoc,
  doc,
  DocumentSnapshot,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  QuerySnapshot,
  Timestamp,
  where,
} from "firebase/firestore"
import { getDoc, getFirestore, setDoc, updateDoc } from "firebase/firestore"
import { deleteObject, getStorage, ref, uploadBytes } from "firebase/storage"
import { uploadBytesResumable } from "firebase/storage"
import { getUid } from "services/auth"
import Blobber from "utils/blobber"
import db, { getActiveInspection, ensureDb, isDbError } from "."
import MediaExsists from "utils/mediaExsists"

type SaveMediaType = {
  name?: string
  group?: string
  path: string
  type: string
} & Partial<MediaType>
export type MediaType = {
  id: string
  name: string
  path: string
  uid: string
  type: string
  url: string
  uploaded: number
  timestamp: Timestamp
  summary?: number
  dataURI?: string
  group?: string
} & Record<string, any>
export function createMediaName() {
  const document = doc(collection(getFirestore(), "media"))

  return document.id
}
export async function saveMedia(
  media: SaveMediaType,
  fn = (media: MediaType) => { },
) {
  const uid = getUid()
  const { path, name } = media
  if (!path) throw new Error("Media path is required")

  const id = path.split("/")[0]
  const document = name
    ? doc(getFirestore(), `media/${name}`)
    : doc(collection(getFirestore(), "media"))
  const url = `${process.env.GATSBY_STORAGE}/${uid}%2F${id}%2F${name || document.id}?alt=media`
  const timestamp = Timestamp.now()
  const newMedia: MediaType = {
    id,
    ...media,
    uid,
    name: name || document.id,
    uploaded: 0,
    url,
    timestamp,
  }

  const performSave = async () => {
    await ensureDb()
    await db.table("media").put(newMedia)
  }

  try {
    try {
      await performSave()
    } catch (error: any) {
      if (isDbError(error)) {
        console.warn("saveMedia: Initial DB save failed, retrying...", error.message)
        for (let attempt = 1; attempt <= 3; attempt++) {
          try {
            await new Promise(res => setTimeout(res, 200 * attempt))
            await performSave()
            console.log(`saveMedia: DB save retry ${attempt} succeeded`)
            break
          } catch (retryError: any) {
            if (attempt === 3) throw retryError
            console.warn(`saveMedia: DB save retry ${attempt} failed:`, retryError.message)
          }
        }
      } else {
        throw error
      }
    }

    // call back function that returns media immediately upon image being saved locally
    fn(newMedia)

    // save reference to firebase
    const cloudMedia: Partial<MediaType> = { ...newMedia }
    delete cloudMedia.dataURI
    setDoc(document, cloudMedia).catch(err => {
      console.error("saveMedia firestore sync failed:", err)
    })

    // attempt to upload
    uploadTask(newMedia, () => { }).catch(err => {
      console.error("saveMedia uploadTask initiation failed:", err)
    })

    return newMedia
  } catch (error: any) {
    const errorMsg = `saveMedia failed: ${error.message || "Unknown error"}`
    console.error(errorMsg, error)
    throw new Error(errorMsg)
  }
}
export async function cloudSaveMedia(
  media: SaveMediaType,
  fn = (media: MediaType) => { },
) {
  const uid = getUid()
  const { path, name } = media
  const id = path.split("/")[0]
  const document = name
    ? doc(getFirestore(), `media/${name}`)
    : doc(collection(getFirestore(), "media"))
  const url = `${process.env.GATSBY_STORAGE}/${uid}%2F${id}%2F${name || document.id}?alt=media`
  const timestamp = Timestamp.now()
  const newMedia: MediaType = {
    id,
    ...media,
    uid,
    name: name || document.id,
    uploaded: 0,
    url,
    timestamp,
  }
  // 1. Instant Local Persistence
  const retrySave = async () => {
    await ensureDb()
    await db.table("media").put(newMedia)
  }

  try {
    await retrySave()
  } catch (error: any) {
    if (isDbError(error)) {
      console.warn("cloudSaveMedia: DB trace failed, retrying...")
      try {
        await new Promise(res => setTimeout(res, 500))
        await retrySave()
      } catch (e) {
        console.error("cloudSaveMedia: DB retry failed", e)
      }
    }
  }

  // 2. Immediate callback
  fn(newMedia)

  // 3. Reliable Background Sync
  const cloudMedia: Partial<MediaType> = { ...newMedia }
  delete cloudMedia.dataURI
  setDoc(document, { ...cloudMedia, timestamp }, { merge: true }).catch(console.error)

  return newMedia
}
export async function upload(media: MediaType, fn = (media: MediaType) => { }) {
  let { path, uid, name, dataURI } = media
  if (!uid) uid = getUid()
  let id = media.id || path.split("/")[0]
  if (!dataURI) return

  // Sanitize UID and ID (prevent "null", "undefined", or empty segments)
  const sanitize = (val: any) => {
    const s = String(val || "").trim().replace(/^\/+|\/+$/g, "")
    return (s === "null" || s === "undefined" || !s) ? "" : s
  }

  uid = sanitize(uid)
  id = sanitize(id)

  if (!uid || !id) {
    console.warn(`upload: Invalid path segments skipping: uid='${uid}', id='${id}'`)
    return
  }

  const storage = ref(getStorage(), `${uid}/${id}/${name}`)
  const blob = Blobber.fromDataURI(dataURI)

  uploadBytes(storage, blob)
    .then(async () => {
      const update = {
        uploaded: 1,
        timestamp: Timestamp.now(),
        id: media.id, // Include inspectionID for Firestore security rules
      }

      setDoc(doc(getFirestore(), `media/${name}`), update, { merge: true }).catch(console.error)

      const retryUpdate = async () => {
        await ensureDb()
        await db.table("media").update(name, { uploaded: 1, dataURI: undefined })
      }

      try {
        await retryUpdate()
      } catch (error: any) {
        if (isDbError(error)) {
          try {
            await new Promise(res => setTimeout(res, 500))
            await retryUpdate()
          } catch (e) {
            console.error("upload update retry failed:", e)
          }
        } else {
          console.error("upload update failed:", error)
        }
      }
    })
    .catch(error => {
      console.error(`Failed to upload media ${name}:`, error)
      // If it's a permission error, we might want to mark it as failed in Dexie 
      // or just let it trigger a retry later. For now, catching prevents the crash.
    })

  fn(media)
  return media
}
export function uploadTask(
  media: MediaType,
  on: ({
    bytesTransferred,
    totalBytes,
  }: {
    bytesTransferred: number
    totalBytes: number
  }) => void,
): Promise<MediaType | null> {
  return new Promise((res, rej) => {
    try {
      const { path, name, dataURI } = media
      let uid = media.uid || getUid()
      let id = media.id || getActiveInspection()

      if (!dataURI) {
        console.warn("uploadTask: Missing dataURI for media", name)
        return res(null)
      }

      // Sanitize UID and ID (prevent "null", "undefined", or empty segments)
      const sanitize = (val: any) => {
        const s = String(val || "").trim().replace(/^\/+|\/+$/g, "")
        return (s === "null" || s === "undefined" || !s) ? "" : s
      }

      uid = sanitize(uid)
      id = sanitize(id)

      if (!uid || !id) {
        const error = `Invalid upload path: uid='${uid}', id='${id}'. Cannot upload.`
        console.error(error)
        return rej(new Error(error))
      }

      console.log(`uploadTask starting: uid=${uid}, id=${id}, name=${name}, dataURILen=${dataURI.length}`)

      const storage = ref(getStorage(), `${uid}/${id}/${name}`)
      const blob = Blobber.fromDataURI(dataURI)
      const document = doc(getFirestore(), `media/${name}`)

      uploadBytesResumable(storage, blob).on(
        "state_changed",
        ({ bytesTransferred, totalBytes }) =>
          on({ bytesTransferred, totalBytes }),
        err => {
          rej(err)
        },
        async () => {
          try {
            const { dataURI } = media

            const retryUpdate = async () => {
              await ensureDb()
              await db.table("media").update(name, { uploaded: 1 })
            }

            try {
              await retryUpdate()
            } catch (error: any) {
              if (isDbError(error)) {
                try {
                  await new Promise(res => setTimeout(res, 500))
                  await retryUpdate()
                } catch (e) {
                  console.error("uploadTask update retry failed:", e)
                }
              } else {
                console.error("uploadTask update failed:", error)
              }
            }

            const mediaClone = { ...media }
            delete mediaClone.dataURI
            await setDoc(document, { ...mediaClone, uploaded: 1 }, { merge: true })

            res({ ...media, uploaded: 1, dataURI })
          } catch (e) {
            rej(e)
          }
        },
      )
    } catch (error) {
      rej(error)
    }
  })
}
export async function getMedia(name: string) {
  if (!name) return null as any
  await ensureDb()
  const document = doc(getFirestore(), `media/${name}`)
  const id = getActiveInspection()
  const media = (await db.table("media").get({ name })) as MediaType

  if (!media) {
    const snapshot = await getDoc(document)

    // media does not exist locally or remotely
    if (!snapshot.exists()) {
      return null
    }
    let media = snapshot.data() as MediaType

    if (media.url.includes("undefined")) {
      //fix it with a update
      const url = `${process.env.GATSBY_STORAGE}/${media.uid}%2F${media.id}%2F${media.name}?alt=media`
      media.url = url
      updateMedia(media.name, { url })
    }

    // Only try to resolve the dataURI if the file has actually finished uploading.
    // This prevents 404/403 errors when one inspector syncs a doc before the other finishes uploading.
    const dataURI = media.uploaded === 1 ? await MediaExsists(media.url) : null
    if (!dataURI) return media

    await db.table("media").put({ ...media, dataURI, name })
    return { ...media, dataURI } as MediaType
  }
  //If stored locally return this instance instead
  return media
}

export function onMedia(name: string, fn: (media: MediaType | null) => void) {
  if (!name) return () => { }
  const document = doc(getFirestore(), `media/${name}`)

  return onSnapshot(document, async snapshot => {
    const localMedia = (await db.table("media").get({ name })) as MediaType
    if (!snapshot.exists()) {
      if (localMedia) return fn(localMedia)
      return fn(null)
    }

    const cloudMedia = snapshot.data() as MediaType
    if (localMedia) {
      // Merge cloud config over local to guarantee `uploaded` flag consistency, while preserving dataURI
      return fn({ ...localMedia, ...cloudMedia })
    }

    fn(cloudMedia)
  })
}

/**
 * Syncs media live across collaborator devices for the active inspection.
 * Listens for new uploads, modifications, and deletions, seamlessly keeping Dexie and the UI up to date.
 */
export function syncMedia() {
  const inspectionId = getActiveInspection()
  if (!inspectionId) return () => { }

  const documents = collection(getFirestore(), "media")
  const q = query(documents, where("id", "==", inspectionId))

  return onSnapshot(q, async (snapshot) => {
    await ensureDb()
    for (const change of snapshot.docChanges()) {
      const data = change.doc.data() as MediaType
      if (!data.name) continue

      if (change.type === "added" || change.type === "modified") {
        const localMedia = await db.table("media").get({ name: data.name }) as MediaType

        // Timestamp validation: Protect local metadata from stale server signals
        if (localMedia && localMedia.timestamp) {
          const localTime = localMedia.timestamp instanceof Timestamp
            ? localMedia.timestamp.toMillis()
            : typeof localMedia.timestamp === 'number' ? localMedia.timestamp : 0

          const remoteTime = data.timestamp instanceof Timestamp
            ? data.timestamp.toMillis()
            : typeof data.timestamp === 'number' ? data.timestamp : 0

          // If local is newer or within a 1-second integrity window, skip update
          if (localTime >= remoteTime || remoteTime < localTime + 1000) {
            // Special case: if server says it's uploaded but local doesn't, we still want to pull dataURI
            if (data.uploaded === 1 && localMedia.uploaded === 0) {
              await db.table("media").update(data.name, { uploaded: 1 })
            }
            continue;
          }
        }

        // Only try to resolve the dataURI if we don't already have it locally and it's NOT yet uploaded (captured locally)
        // For already uploaded remote media, we rely on the `url` to avoid memory bloat from Base64 strings.
        const dataURI = localMedia?.dataURI
        if (dataURI) {
          await db.table("media").put({ ...data, dataURI })
        } else {
          await db.table("media").put(data)
        }
      }

      if (change.type === "removed") {
        await db.table("media").delete(data.name)
      }
    }
  })
}

export async function getGroupedMedia(key: string, values: string[]) {
  const id = getActiveInspection()
  if (!id) return []

  await ensureDb()
  // 1. Get all local media for this inspection that matches the criteria
  const localMedia = await db
    .table("media")
    .where({ id })
    .and((media: MediaType) => {
      if (!media.group) return false
      return values.includes(media.group)
    })
    .toArray()

  // 2. Map local media for quick lookup
  const mediaMap = new Map<string, MediaType>()
  localMedia.forEach(m => mediaMap.set(m.name, m))

  try {
    // 3. Query Firestore
    const documents = collection(getFirestore(), "media")
    const q = query(documents, where("id", "==", id), where(key, "in", values))
    const snapshot = await getDocs(q)

    snapshot.docs.forEach(doc => {
      const cloudData = doc.data() as MediaType
      const localData = mediaMap.get(cloudData.name)

      if (localData) {
        // Merge cloud data over local, but KEEP local dataURI
        mediaMap.set(cloudData.name, { ...localData, ...cloudData, dataURI: localData.dataURI })
      } else {
        // New from cloud
        mediaMap.set(cloudData.name, cloudData)
        // Background fetch dataURI if missing
        MediaExsists(cloudData.url).then(dataURI => {
          if (dataURI) db.table("media").put({ ...cloudData, dataURI })
        })
      }
    })
  } catch (error) {
    console.error("Failed to fetch grouped media from cloud:", error)
    // If cloud fails, we still have local media in the map
  }

  return Array.from(mediaMap.values())
}
export async function getMediaAt(
  path: string,
  remote: boolean = false, //get remote
): Promise<MediaType[]> {
  const id = getActiveInspection()
  if (!id) return []

  await ensureDb()
  // 1. Get local media
  const localMedia = await db
    .table("media")
    .where({ id, path })
    .toArray()

  // 2. Map for merging
  const mediaMap = new Map<string, MediaType>()
  localMedia.forEach(m => mediaMap.set(m.name, m))


  try {
    const documents = collection(getFirestore(), "media")
    const q = query(
      documents,
      where("id", "==", id),
      where("path", "==", path),
    )
    // 3. Fetch from Cloud with timeout to prevent hanging on poor connections
    const querySnapshots = await Promise.race([
      getDocs(q),
      new Promise<never>((_, rej) =>
        setTimeout(() => rej(new Error("Firestore fetch timed out")), 5000)
      ),
    ])

    for (const doc of querySnapshots.docs) {
      const cloudData = doc.data() as MediaType
      const localData = mediaMap.get(cloudData.name)

      if (localData) {
        mediaMap.set(cloudData.name, { ...localData, ...cloudData, dataURI: localData.dataURI })
      } else {
        // Only check for existence if uploaded, otherwise skip fetch to prevent 404/403 console errors
        const dataURI = cloudData.uploaded === 1 ? await MediaExsists(cloudData.url) : null
        const finalMedia = dataURI ? { ...cloudData, dataURI } : cloudData
        mediaMap.set(cloudData.name, finalMedia)
        if (dataURI) db.table("media").put(finalMedia)
      }
    }
  } catch (error) {
    console.error("Failed to fetch media at path from cloud:", error)
  }

  return Array.from(mediaMap.values())
}
export async function removeMedia(name: string) {
  if (!name) return
  const media = await getMedia(name)

  if (!media) throw new Error(`Media ${name} does not exists`)
  const { path, uid } = media

  // const id = path.split("/")[0]
  const id = getActiveInspection()
  const storage = ref(getStorage(), `${uid}/${id}/${name}`)

  const retryDelete = async () => {
    await ensureDb()
    await db.table("media").delete(name)
  }

  try {
    await retryDelete()
  } catch (error: any) {
    if (isDbError(error)) {
      try {
        await new Promise(res => setTimeout(res, 500))
        await retryDelete()
      } catch (e) {
        console.error("removeMedia retry failed:", e)
      }
    } else {
      console.error("removeMedia failed:", error)
    }
  }

  deleteObject(storage).catch(console.error)
  deleteDoc(doc(getFirestore(), `media/${name}`)).catch(console.error)

  // Dispatch global event for real-time UI synchronization
  window.dispatchEvent(
    new CustomEvent("media-deleted", { detail: { name, path } }),
  )

  return name
}
export async function updateMedia(
  name: string,
  update: { [key: string]: any },
) {
  if (!name) return ""

  const modified = Timestamp.now()
  const fullUpdate = { ...update, timestamp: modified }

  const retryUpdate = async () => {
    await ensureDb()
    await db.table("media").update(name, fullUpdate)
  }

  try {
    await retryUpdate()
  } catch (error: any) {
    if (isDbError(error)) {
      try {
        await new Promise(res => setTimeout(res, 500))
        await retryUpdate()
      } catch (e) {
            console.error("updateMedia retry failed:", e)
      }
    }
  }

  // Sync to Firestore immediately and in the background.
  // We MUST include the 'id' (inspectionID) field in the sync payload.
  // Firestore Security Rules use this ID to verify the user has access to the
  // linked inspection. If the document is missing (e.g. initial sync is pending),
  // a partial update without this ID will be rejected.
  const local = await db.table("media").get({ name }) as MediaType
  const inspectionID = local?.id || getActiveInspection()
  const merged = { ...local, ...fullUpdate, id: inspectionID }

  const db_fire = getFirestore()
  const document = doc(db_fire, `media/${name}`)

  const cloudMerged = { ...merged }
  delete cloudMerged.dataURI

  setDoc(document, cloudMerged, { merge: true }).catch(console.error)

  return name
}

export async function copyMedia(media: MediaType, location: string) {
  const db_fire = getFirestore()
  const modified = Timestamp.now()
  const name = createMediaName()
  const { type, path, uid } = media
  const dataURI = media.dataURI || (await MediaExsists(media.url))
  // const mediaDocument = doc(getFirestore(), `media/${name}`)
  const [id, lid] = path.split("/")

  const document = doc(db_fire, `inspections/${id}`)

  if (!dataURI) return

  const newMedia = { name, path, type, dataURI, group: lid }

  const mediaCopy = await saveMedia(newMedia)

  setDoc(document, { [lid]: { resources: arrayUnion(name) }, modified }, { merge: true }).catch(console.error)

  return mediaCopy
}
export function useGetMedia(name: string) {
  const [media, setMedia] = useState<MediaType | null>(null)

  useEffect(() => {
    if (media) return
    getMedia(name).then(setMedia)
  }, [media])
  return media
}
export async function getAllRemoteMedia() {
  const id = getActiveInspection()
  const documents = collection(getFirestore(), `media`)
  const q = query(documents, where("id", "==", id))

  const querySnapshots = await getDocs(q)

  if (querySnapshots.empty) return []

  return querySnapshots.docs.map(doc => doc.data() as MediaType)
}
//these functions get images saved locally
export async function getAllMedia(params?: Partial<MediaType>) {
  const id = getActiveInspection()
  return (await db
    .table("media")
    .where({ ...params, id })
    .toArray()) as MediaType[]
}
export async function unuploadedMedia() {
  const id = getActiveInspection()
  const uid = getUid()

  // If no inspection is active, we can't query by [id + uploaded]
  // We should either return an empty array or query all unuploaded media
  if (!id) {
    const unuploaded = await db
      .table("media")
      .where({ uploaded: 0 })
      .toArray()
    return unuploaded.filter(
      item => item.uid === uid && item.dataURI,
    ) as MediaType[]
  }

  const unuploaded = await db
    .table("media")
    .where("[id+uploaded]")
    .equals([id, 0])
    .toArray()

  return unuploaded.filter(
    item => item.uid === uid && item.dataURI,
  ) as MediaType[]
}

export async function getUnuploadedMediaCount(): Promise<number> {
  const id = getActiveInspection()
  const uid = getUid()

  await ensureDb()

  if (!id) {
    return await db.table("media")
      .where({ uploaded: 0 })
      .filter((item: MediaType) => item.uid === uid && !!item.dataURI)
      .count()
  }

  return await db.table("media")
    .where("[id+uploaded]")
    .equals([id, 0])
    .filter((item: MediaType) => item.uid === uid && !!item.dataURI)
    .count()
}

export async function getNextUnuploadedMedia(): Promise<MediaType | null> {
  const id = getActiveInspection()
  const uid = getUid()

  await ensureDb()

  let nextMedia: MediaType | null = null;

  if (!id) {
    await db.table("media")
      .where({ uploaded: 0 })
      .filter((item: MediaType) => item.uid === uid && !!item.dataURI)
      .until(() => nextMedia !== null)
      .each((item: MediaType) => {
        nextMedia = item
      })
    return nextMedia
  }

  await db.table("media")
    .where("[id+uploaded]")
    .equals([id, 0])
    .filter((item: MediaType) => item.uid === uid && !!item.dataURI)
    .until(() => nextMedia !== null)
    .each((item: MediaType) => {
      nextMedia = item
    })

  return nextMedia
}
export function useGetAllMedia(
  params: Partial<MediaType>,
  fn: () => void = () => { },
) {
  const [media, setMedia] = useState<MediaType[]>()
  useEffect(() => {
    if (media) {
      fn()
      return
    }
    getAllMedia(params).then(setMedia)
  }, [media, params])
  return media
}
export function useGetUnuploadedMedia(fn: () => void = () => { }) {
  const [media, setMedia] = useState<MediaType[]>()
  useEffect(() => {
    if (media) {
      fn()
      return
    }

    unuploadedMedia().then(setMedia)
  }, [media])
  return media
}
